import { AdoptionProgress, AdoptionProgressReporter, adoptReportDownload } from "./adoptReportDownload";
import { ReportSourcePayload, REPORT_RENDER_VERSION, mutateReportSource,
  readSectionSnapshot, readSectionSnapshots, sectionFingerprint } from "./reportSourcePayload";
import { RenderSectionOptions, renderSectionContent } from "./renderSectionContent";
import { buildGroundingReferenceBlocks, JENLY_REFERENCE_TAG_PREFIX, stripGroundingMarkerBrackets } from "./groundingMarkers";
import { loadFontStyle } from "@/lib/styleService";
import Logger from "@/utility/logger";
import { formatTableCellLists } from "@/hooks/useWordDocumentUtils/wordFormatting";

type ImageServices = Pick<RenderSectionOptions, "retrieveDocumentsForImageInsertion" | "retrieveDocumentImages" | "downloadDocumentImage">;

export function reportRenderIsCurrent(sections: any[]): boolean {
  return sections.every((section) => section.render_state?.version === REPORT_RENDER_VERSION &&
    (section.render_state.complete || section.render_state.preserved));
}

export async function probeReportRender(sections: any[]): Promise<boolean> {
  if (!reportRenderIsCurrent(sections)) return false;
  for (const section of sections) {
    if (section.render_state?.preserved) continue;
    try { await verifySectionGrounding(section); }
    catch (_) { return false; }
  }
  return true;
}

/**
 * Downloaded reports reach the reparse with stray square
 * brackets hugging their grounding diamonds. Only a full reparse strips them,
 * so ordinary in-add-in generation keeps its existing output byte-for-byte.
 */
const withoutDiamondBrackets = (markdown: string): string => stripGroundingMarkerBrackets(markdown || "");

function validateGrounding(section: any) {
  const markdown = section.grounding?.markdown || section.content;
  const references = Array.from(markdown.matchAll(/\[\^([SR]\d+)\]/g), (match: any) => match[1]);
  if (!references.length) return;
  if (!section.grounding || !section.agent_task_id) throw new Error(`Grounding is unavailable for ${section.display_name || section.insertion_key}.`);
  const sources = new Set((section.grounding.sources || []).map((record) => record.id));
  const rationales = new Map((section.grounding.rationales || []).map((record) => [record.id, record]));
  for (const id of references) {
    if (!(id.startsWith("S") ? sources.has(id) : rationales.has(id))) {
      throw new Error(`Grounding citation ${id} has no record in ${section.display_name || section.insertion_key}.`);
    }
    if (id.startsWith("R")) {
      const rationale: any = rationales.get(id);
      if ((rationale.source_ids || []).some((sourceId) => !sources.has(sourceId))) {
        throw new Error(`Grounding rationale ${id} references missing evidence in ${section.display_name || section.insertion_key}.`);
      }
    }
  }
}

export async function verifySectionGrounding(section: any): Promise<void> {
  const expected = new Map<string, number>();
  if (section.grounding && section.agent_task_id) {
    for (const block of buildGroundingReferenceBlocks(section.agent_task_id, section.grounding.markdown || section.content)) {
      expected.set(block.tag, (expected.get(block.tag) || 0) + 1);
    }
  }
  await Word.run(async (context) => {
    const start = context.document.getBookmarkRangeOrNullObject(`${section.insertion_key}_START`);
    const end = context.document.getBookmarkRangeOrNullObject(`${section.insertion_key}_END`);
    start.load("isNullObject"); end.load("isNullObject");
    await context.sync();
    if (start.isNullObject || end.isNullObject) throw new Error(`Section bookmarks are missing for ${section.insertion_key}.`);
    const controls = start.expandTo(end).contentControls;
    controls.load("items/tag");
    await context.sync();
    const actual = new Map<string, number>();
    for (const control of controls.items) {
      const tag = control.tag.replace(/:a\d+$/, "");
      if (tag.startsWith(JENLY_REFERENCE_TAG_PREFIX + ":")) actual.set(tag, (actual.get(tag) || 0) + 1);
    }
    if (actual.size !== expected.size || Array.from(expected).some(([tag, count]) => actual.get(tag) !== count)) {
      throw new Error(`Word did not retain every grounding link in ${section.display_name || section.insertion_key}.`);
    }
  });
}

async function replaceSectionWithBackup(key: string, ooxml: string): Promise<void> {
  await Word.run(async (context) => {
    const start = context.document.getBookmarkRangeOrNullObject(`${key}_START`);
    const end = context.document.getBookmarkRangeOrNullObject(`${key}_END`);
    start.load("isNullObject"); end.load("isNullObject");
    await context.sync();
    if (start.isNullObject || end.isNullObject) throw new Error("Word removed the section bookmarks; reopen a saved copy before retrying.");
    start.expandTo(end).insertOoxml(ooxml, Word.InsertLocation.replace);
    await context.sync();
  });
}

export const SECTION_PROCESSING_WEIGHT = 10;

/** First-open replay is conservative. Only an explicit full reparse may replace edited sections. */
export async function reparseReportDownload({ payload, sections, services, onProgress, force = false, reviewedIds = [], passId }: {
  payload: ReportSourcePayload; sections: any[]; services: ImageServices;
  onProgress?: AdoptionProgressReporter; force?: boolean; reviewedIds?: string[]; passId?: string;
}): Promise<{
  changed: boolean;
  preserved: string[];
  warnings: string[];
  /** User-edited sections whose citation anchors could not be re-linked
   * because the edited text no longer carries them (expected, informational). */
  skippedCitationSections: string[];
}> {
  const counts = sections.map((section) => section.grounding && section.agent_task_id
    ? buildGroundingReferenceBlocks(section.agent_task_id, section.grounding.markdown || section.content).length : 0);
  const total = 3 + sections.length * SECTION_PROCESSING_WEIGHT + counts.reduce((sum, count) => sum + count, 0);
  const complete: number[] = [];
  const preservedIndices: number[] = [];
  const preserved: string[] = [];
  const warnings: string[] = [];
  const skippedCitationSections: string[] = [];
  let done = 0;
  let changed = false;
  const report = (stage: AdoptionProgress["stage"], label: string, index: number | null = null) => onProgress?.({
    stage, label, processed: complete.length, total: sections.length,
    sectionsTotal: sections.length, sectionsDone: complete.length, sectionsComplete: [...complete],
    sectionsPreserved: [...preservedIndices], unitsDone: done, unitsTotal: total,
    sectionIndex: index, sectionName: index === null ? null : sections[index].display_name || sections[index].insertion_key,
    sectionMarkersDone: index === null ? 0 : complete.includes(index) ? counts[index] : 0,
    sectionMarkersTotal: index === null ? 0 : counts[index],
  });
  report("inspect", "Reading your report…");
  // Complete preflight before the first destructive section operation.
  // Word hosts can reject overlapping calls, even across request contexts.
  const snapshots: Awaited<ReturnType<typeof readSectionSnapshot>>[] = [];
  try {
    const snapshotsMap = await readSectionSnapshots(sections.map((s) => s.insertion_key));
    for (const section of sections) {
      const snap = snapshotsMap?.get(section.insertion_key);
      if (snap) snapshots.push(snap);
      else snapshots.push(await readSectionSnapshot(section.insertion_key));
    }
  } catch (_) {
    for (const section of sections) {
      snapshots.push(await readSectionSnapshot(section.insertion_key));
    }
  }
  for (const section of sections) validateGrounding(section);
  const draftStyles = await loadFontStyle("draftStyles");
  done += 1; report("inspect", "Reading your report…");

  for (const [index, section] of Array.from(sections.entries())) {
    const savedState = section.render_state;
    if ((!force || (passId && savedState?.pass_id === passId)) && savedState?.version === REPORT_RENDER_VERSION && (savedState.complete || savedState.preserved)) {
      let retained = savedState.preserved;
      if (!retained) {
        try { await verifySectionGrounding(section); retained = true; }
        catch (_) { /* Rebuild only if the content signature still matches. */ }
      }
      if (retained) {
        complete.push(index); done += SECTION_PROCESSING_WEIGHT + counts[index];
        if (savedState.preserved) { preserved.push(section.display_name || section.insertion_key); preservedIndices.push(index); }
        report("render", "Restoring each section…", index); continue;
      }
    }
    const snapshot = snapshots[index];
    const fingerprint = await sectionFingerprint(snapshot.ooxml, section.insertion_key);
    const baseline = savedState?.fingerprint || section.initial_fingerprint;
    const untouched = Boolean(baseline && baseline === fingerprint);
    if (reviewedIds.includes(section.settings_group_id) || (!force && (!untouched || savedState?.in_progress))) {
      preserved.push(section.display_name || section.insertion_key); preservedIndices.push(index);
      // Retain Word edits while restoring surviving assembler citation anchors.
      // Do not apply a font to the whole section or change a review decision.
      if (counts[index]) {
        try {
          try { await verifySectionGrounding(section); }
          catch (_) {
            await adoptReportDownload(payload.download_id, [section], undefined, {
              applyStyling: false, saveState: false,
              appearance: reviewedIds.includes(section.settings_group_id)
                ? Word.ContentControlAppearance.hidden : Word.ContentControlAppearance.boundingBox,
            });
            await verifySectionGrounding(section);
          }
          changed = true;
        } catch (error) {
          // This branch is only reached for sections the USER edited
          // (or explicitly reviewed). Their text may legitimately no longer
          // carry every citation anchor, so an unrestore-able anchor is
          // expected — reporting it as a warning produced a scary
          // "could not be restored" banner right after linking completed.
          // Keep it as an informational skip, not a warning.
          Logger.warn("reparseReportDownload: skipped citation re-link for edited section", {
            section: section.display_name || section.insertion_key,
            error: error instanceof Error ? error.message : String(error),
          });
          skippedCitationSections.push(section.display_name || section.insertion_key);
        }
      }
      // This section keeps the assembler's own table markup,
      // which carries "- " list text instead of real Word bullets. Reuse the
      // add-in's conversion so a reparse looks like in-add-in generation.
      // Runs BEFORE the snapshot so the saved fingerprint matches the text.
      await formatTableCellLists(section.insertion_key);
      const preservedSnapshot = await readSectionSnapshot(section.insertion_key);
      const preservedFingerprint = await sectionFingerprint(preservedSnapshot.ooxml, section.insertion_key);
      await mutateReportSource((source) => {
        if (!source || source.download_id !== payload.download_id) throw new Error("Report source changed during restoration.");
        source.sections.find((item) => item.settings_group_id === section.settings_group_id).render_state = {
          ...savedState, version: REPORT_RENDER_VERSION, preserved: true,
          pass_id: passId,
          text: preservedSnapshot.text, fingerprint: preservedFingerprint,
        };
        return source;
      });
      changed = true;
      complete.push(index); done += SECTION_PROCESSING_WEIGHT + counts[index];
      report("render", "Preserving your existing section…", index); continue;
    }
    report("render", "Rebuilding each section…", index);
    await mutateReportSource((source) => {
      if (!source || source.download_id !== payload.download_id) throw new Error("Report source changed during restoration.");
      source.sections.find((item) => item.settings_group_id === section.settings_group_id).render_state = {
        version: REPORT_RENDER_VERSION, in_progress: true, text: snapshot.text, fingerprint,
        pass_id: passId,
        backup_ooxml: snapshot.ooxml,
      };
      return source;
    });
    try {
      await renderSectionContent({
        ...services, section: { id: section.settings_group_id, insertion_key: section.insertion_key,
          display_name: section.display_name, type: section.section_type },
        projectId: payload.project_id, markdown: withoutDiamondBrackets(section.content),
        grounding: section.grounding && { ...section.grounding,
          markdown: withoutDiamondBrackets(section.grounding.markdown || "") },
        groundingTaskId: section.agent_task_id, imageAssets: section.image_assets, draftStyles, persistSource: false, replaceExisting: true,
      });
      await verifySectionGrounding(section);
      const rendered = await readSectionSnapshot(section.insertion_key);
      const renderedFingerprint = await sectionFingerprint(rendered.ooxml, section.insertion_key);
      await mutateReportSource((source) => {
        if (!source || source.download_id !== payload.download_id) throw new Error("Report source changed during restoration.");
        source.sections.find((item) => item.settings_group_id === section.settings_group_id).render_state = {
          version: REPORT_RENDER_VERSION, complete: true, text: rendered.text, fingerprint: renderedFingerprint,
          pass_id: passId,
        };
        return source;
      });
      changed = true;
    } catch (error) {
      try {
        await replaceSectionWithBackup(section.insertion_key, snapshot.ooxml);
        await mutateReportSource((source) => {
          if (!source || source.download_id !== payload.download_id) throw new Error("Report source changed during restoration.");
          source.sections.find((item) => item.settings_group_id === section.settings_group_id).render_state = savedState;
          return source;
        });
      } catch (restoreError) {
        throw new Error(`${error.message} The previous section could not be restored: ${restoreError.message}`);
      }
      throw error;
    }
    complete.push(index); done += SECTION_PROCESSING_WEIGHT + counts[index];
    report("render", "Rebuilding each section…", index);
  }
  // Each rendered section was verified before its completed state was saved.
  done += 1; report("verify", "Checking restored sections…");
  await Word.run(async (context) => {
    const props = context.document.properties.customProperties;
    const state = props.getItemOrNullObject("jenlyReportRenderVersion");
    state.load("isNullObject");
    await context.sync();
    if (state.isNullObject) props.add("jenlyReportRenderVersion", JSON.stringify(REPORT_RENDER_VERSION));
    else state.value = JSON.stringify(REPORT_RENDER_VERSION);
    await context.sync();
  });
  done += 1; report("save", "Finishing up…");
  return { changed, preserved, warnings, skippedCitationSections };
}
